import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const VehiclesAndAssets = () => (
  <Page
    title="Vehicles"
    intro="The vehicle list is the home page for the fleet: every vehicle's status, cleaning, checks, and servicing position at a glance, with a full record behind each one. Equipment is managed separately in the Asset Manager."
  >
    <Section title="The vehicle list">
      <Bullets>
        <Bullet>
          Open <DocLink url="/fleet" text="Vehicles" /> for the fleet list.
          The <strong>Overview</strong> tab shows each vehicle's call sign,
          image, registration, status, type, base location, open defect
          count, LOLER position, and its last deep clean, safety check, and
          service, alongside MOT, tax, and insurance columns.
        </Bullet>
        <Bullet>
          The last deep clean, safety check, and service dates turn{' '}
          <strong>orange</strong> when the next one is due within a week and{' '}
          <strong>red</strong> once overdue, based on the frequencies set in{' '}
          <DocLink
            url="/site-configuration/vehicles"
            text="Configuration > Fleet > Vehicles"
          />
          . The <strong>MOT</strong>, <strong>TAX</strong>, and{' '}
          <strong>Insurance</strong> columns highlight the same way as expiry
          approaches, and the <strong>LOLER</strong> column shows a coloured
          chip for anything due soon, overdue, failed, or never examined.
        </Bullet>
        <Bullet>
          A vehicle's status is one of <strong>Operational</strong>,{' '}
          <strong>Out of Service</strong>, or{' '}
          <strong>Decontamination Required</strong>, and can be changed from
          the status chip on the vehicle record.
        </Bullet>
        <Bullet>
          Both the Overview and Availability tabs have a print button that
          produces a PDF of the current view for handovers or noticeboards.
        </Bullet>
      </Bullets>
      <Screenshot url="fleet-equipment/vehicles-list.png">
        The vehicle list Overview tab with status, compliance, and expiry
        columns for every vehicle.
      </Screenshot>
      <Info title="Who can do this?">
        The Vehicles pages appear in the sidebar for the Fleet, Make Ready,
        Manager, and Supervisor teams (and site administrators). The server
        accepts vehicle reads and edits from any signed-in account, so links
        into a vehicle record from elsewhere in AmbuNet always work.
      </Info>
    </Section>

    <Section title="Availability view">
      <p>
        Switch the vehicle list to the <strong>Availability</strong> tab for a
        week-at-a-glance timeline of what every vehicle is committed to — PTS
        duties, event deployments, and planned downtime windows. Click a
        vehicle or any of its blocks to open the vehicle record.
      </p>
    </Section>

    <Section title="Adding a vehicle">
      <Step number={1} title="Start a new vehicle">
        On <DocLink url="/fleet" text="Vehicles" />, click{' '}
        <strong>New</strong>.
      </Step>
      <Step number={2} title="Fill in the basics">
        Enter the call sign and number plate, pick the vehicle type and base
        location, and (optionally) choose the Make Ready load list the
        vehicle should be checked against and upload an image.
      </Step>
      <Step number={3} title="Let DVLA fill in the rest">
        Once saved, AmbuNet looks the registration up with the DVLA and shows
        live MOT expiry, tax due date, and vehicle details on the record —
        there is nothing to type in for those.
      </Step>
    </Section>

    <Section title="The vehicle record">
      <Bullets>
        <Bullet>
          The record opens with a <strong>hero</strong>: the vehicle's image,
          call sign, number plate, and status chip, with stat tiles for{' '}
          <strong>MOT</strong> and <strong>Tax</strong> (live from the DVLA),{' '}
          <strong>Last Service</strong>, <strong>Last Clean</strong> (with
          the last deep clean as its caption), <strong>Last Safety
          Check</strong>, <strong>LOLER</strong> (when the vehicle has
          lifting equipment — click it to jump to the Safety Checks tab),
          and <strong>Active Defects</strong>.
        </Bullet>
        <Bullet>
          The hero also warns you: a red <strong>Unavailable</strong> chip
          appears while a downtime window is running, an orange chip counts
          any planned services whose window has passed without a service
          being recorded (click either to open the Planned Downtime tab),
          and <strong>click to fix</strong> warning chips appear when the
          Make Ready load list or base location is not set.
        </Bullet>
        <Bullet>
          Quick-action buttons on the hero start the common jobs —{' '}
          <strong>Defect</strong>, <strong>Clean</strong>,{' '}
          <strong>Safety Check</strong>, <strong>Service</strong>, and{' '}
          <strong>Make Ready</strong> — and the <strong>More</strong> menu
          holds <strong>Edit Vehicle</strong>,{' '}
          <strong>Schedule Downtime</strong>, and{' '}
          <strong>Archive Vehicle</strong>.
        </Bullet>
        <Bullet>
          Everything else lives in tabs below the hero:{' '}
          <strong>Overview</strong>, <strong>Deployments</strong>,{' '}
          <strong>Defects</strong>, <strong>Servicing &amp; MOT</strong>,{' '}
          <strong>Cleaning</strong>, <strong>Fuel</strong>,{' '}
          <strong>Safety Checks</strong>, <strong>Assets</strong>,{' '}
          <strong>Make Ready</strong>, <strong>Planned Downtime</strong>,{' '}
          <strong>PTS</strong> (on PTS subscriptions), <strong>Files</strong>,
          and <strong>Notes</strong>.
        </Bullet>
        <Bullet>
          Tabs flag their own problems: the Defects tab counts active
          defects, the Fuel tab warns when a fuel card is expiring or
          expired, the Safety Checks tab carries a LOLER badge when an
          examination is due, overdue, or failed, and the Planned Downtime
          tab counts its windows (red once planned work is overdue).
        </Bullet>
      </Bullets>
      <Screenshot url="fleet-equipment/vehicle-record.png">
        A vehicle record's hero with its stat tiles and quick actions above the
        tabs.
      </Screenshot>
    </Section>

    <Section title="Servicing, MOT, and insurance">
      <Bullets>
        <Bullet>
          Record services, MOT results, and insurance policies from the{' '}
          <strong>Servicing &amp; MOT</strong> tab — or start a service
          straight from the hero's <strong>Service</strong> button. The Add
          Service popup takes the date, garage, and cost, and invoices,
          job sheets, and other paperwork can be attached to the service
          itself.
        </Bullet>
        <Bullet>
          The <strong>Costs</strong> panel totals what has been spent on
          services, MOTs, and insurance for the vehicle.
        </Bullet>
      </Bullets>
      <Info title="MOT and tax are checked live">
        MOT expiry and tax due dates come straight from the DVLA lookup on
        the registration, so they stay correct without manual entry —
        recorded MOTs are for your own paperwork and cost history.
      </Info>
    </Section>

    <Section title="Fuel">
      <Bullets>
        <Bullet>
          Crew record a refuel from{' '}
          <DocLink url="/record-fuel" text="Record Fuel" /> (also linked from
          the dashboard): pick the vehicle, enter the litres added, price per
          litre, total cost (calculated from litres × price until you type
          your own), and mileage, and attach a photo of the receipt. Any
          signed-in crew member can record fuel.
        </Bullet>
        <Bullet>
          The vehicle record's <strong>Fuel</strong> tab shows the full
          refuel log with 30-day stats — fills, litres, and spend — and a
          button to record a fill against that vehicle. A fuel record can be
          deleted by the person who recorded it within 24 hours, or by the
          Fleet or Make Ready team at any time.
        </Bullet>
        <Bullet>
          The Fuel tab also holds the vehicle's <strong>fuel cards</strong> —
          provider, card number (masked until revealed), and expiry month.
          An expiring or expired card flags the Fuel tab itself, so it gets
          replaced before crew are stuck at the pump. Adding, editing, and
          removing fuel cards requires the <strong>Fleet</strong> or{' '}
          <strong>Make Ready</strong> team.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="LOLER inspections">
      <Bullets>
        <Bullet>
          Thorough examinations of lifting equipment — tail lifts, wheelchair
          lifts, ramp winches, stretcher loading systems, hoists — are
          recorded on the <strong>Safety Checks</strong> tab. Each record
          holds the equipment (type, description, serial number, and safe
          working load), the examination type and date, the next examination
          due date, the competent person and their company, the report or
          certificate number, and the outcome (Pass, Pass with observations,
          or Fail), with the examination report and certificates attached.
        </Bullet>
        <Bullet>
          The next-due date defaults to 6 months after the examination for
          equipment that lifts people and 12 months otherwise, and can be
          overridden where a written scheme of examination sets its own
          interval. Defects or observations are recorded with a{' '}
          <strong>put right by</strong> date — a failed examination means the
          equipment must not be used until the defect is fixed.
        </Bullet>
        <Bullet>
          Re-examinations of the same equipment build up its history, and the
          vehicle's LOLER position (in date, due soon, overdue, failed, or
          never examined) shows on the hero tile, the Safety Checks tab
          badge, and the vehicle list's LOLER column. Equipment that has been
          removed can be <strong>retired</strong> so it stops counting
          against the vehicle — and tracked again later if it comes back.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Planned downtime">
      <Bullets>
        <Bullet>
          Use <strong>Schedule Downtime</strong> (on the hero's More menu, or
          from the <strong>Planned Downtime</strong> tab) to block out a
          window with a start, end, and reason. Each window has a type —{' '}
          <strong>Planned Service</strong>, <strong>Repair</strong>,{' '}
          <strong>Inspection/MOT</strong>, or <strong>Other</strong> — or can
          be left as plain <strong>Unavailable</strong> for downtime with no
          planned work.
        </Bullet>
        <Bullet>
          If the window clashes with anything the vehicle is already
          committed to — PTS duties, event deployments, or another downtime
          window — the conflicts are listed before anything is saved, and can
          be confirmed anyway. Downtime windows show in red on the
          Availability view.
        </Bullet>
        <Bullet>
          When the work is done, <strong>Mark Complete</strong> on the
          Planned Downtime tab records the completed work as a service on the
          vehicle in the same step. A typed window whose end passes without a
          service being recorded shows as an orange warning chip on the hero
          until it is completed or rescheduled.
        </Bullet>
      </Bullets>
      <Screenshot url="fleet-equipment/planned-downtime.png">
        The Planned Downtime tab with a vehicle's scheduled windows and their
        types.
      </Screenshot>
      <Info title="Reminders">
        The Fleet and Make Ready teams are reminded about planned work —
        services, repairs, and inspections — 7 days before the window starts
        and again on the day. Plain Unavailable windows with no type are not
        planned work, so they get no reminders.
      </Info>
    </Section>

    <Section title="Archiving and restoring">
      <p>
        Archiving removes a vehicle from the day-to-day fleet list without
        deleting its history. Archived vehicles can be reviewed and restored
        from <strong>Configuration &gt; Fleet &gt; Vehicles</strong>.
      </p>
      <Warning title="Who can do this?">
        Viewing the archived list and restoring a vehicle requires the{' '}
        <strong>Fleet</strong> team (site administrators always can).
      </Warning>
    </Section>

    <Section title="Equipment">
      <p>
        Non-vehicle equipment — defibrillators, bags, carry chairs, radios,
        and so on — is managed in the Asset Manager. See{' '}
        <DocLink
          url="/fleet-equipment/asset-manager"
          text="Asset Manager"
          docsPage
        />
        .
      </p>
    </Section>
  </Page>
)

export default VehiclesAndAssets
